
import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Save, AlertCircle } from "lucide-react";
import { User } from "@/api/entities";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { appClient } from "@/api/appClient";
import { supabase } from "@/integrations/supabase/client";

export default function LeadForm({ lead, onSubmit, onCancel, isProcessing, initialPhone }) {
  const [user, setUser] = useState(null);
  const [availableSources, setAvailableSources] = useState([]);
  const [availableMarkets, setAvailableMarkets] = useState([]);
  const [phoneError, setPhoneError] = useState("");

  const parsePhoneNumberForState = (fullPhoneNumber) => {
    if (!fullPhoneNumber) return { country_code: "+1", phone_number: "" };

    if (fullPhoneNumber.startsWith("+1")) {
      return { country_code: "+1", phone_number: fullPhoneNumber.substring(2) };
    }
    if (fullPhoneNumber.startsWith("+55")) {
      return { country_code: "+55", phone_number: fullPhoneNumber.substring(3) };
    }
    return { country_code: "+1", phone_number: fullPhoneNumber };
  };

  const initialPhoneParsed = parsePhoneNumberForState(initialPhone || lead?.phone);

  const [formData, setFormData] = useState({
    first_name: lead?.first_name || "",
    last_name: lead?.last_name || "",
    preferred_name: lead?.preferred_name || "",
    email: lead?.email || "",
    phone: initialPhoneParsed.phone_number,
    country_code: initialPhoneParsed.country_code,
    source: lead?.source || "website",
    market: lead?.market || "",
    date_of_birth: lead?.date_of_birth || "",
    gender: lead?.gender || "",
    language: lead?.language || "",
    country_of_birth: lead?.country_of_birth || "",
    address_line1: lead?.address?.street || "",
    address_line2: lead?.address?.line2 || "",
    city: lead?.address?.city || "",
    state: lead?.address?.state || "",
    postal_code: lead?.address?.zip || "",
    country: lead?.address?.country || ""
  });

  const loadUser = useCallback(async () => {
    try {
      const userData = await User.me();
      setUser(userData);
      
      const sources = userData.module_settings?.leads?.custom_lead_sources || [
        { id: "website", name: "Website" },
        { id: "referral", name: "Referral" },
        { id: "social_media", name: "Social Media" },
        { id: "email_campaign", name: "Email Campaign" },
        { id: "phone_call", name: "Phone Call" },
        { id: "walk_in", name: "Walk In" },
        { id: "other", name: "Other" }
      ];
      setAvailableSources(sources);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  }, []);

  const loadMarkets = useCallback(async () => {
    try {
      const marketsSet = new Set();
      const { data: leadsMarkets } = await supabase
        .from('leads')
        .select('market')
        .not('market', 'is', null)
        .limit(1000);
      (leadsMarkets || []).forEach((r) => r.market && marketsSet.add(r.market));
      
      const { data: campaignMarkets } = await supabase
        .from('ad_campaigns')
        .select('market')
        .not('market', 'is', null)
        .limit(1000);
      (campaignMarkets || []).forEach((r) => r.market && marketsSet.add(r.market));

      const markets = Array.from(marketsSet);
      setAvailableMarkets(markets);
      if (!formData.market && markets.length) {
        setFormData((prev) => ({ ...prev, market: markets[0] }));
      }
    } catch (error) {
      console.error('Error loading markets:', error);
      // fallback seguro para não travar
      if (!formData.market) {
        setFormData((prev) => ({ ...prev, market: 'US' }));
      }
    }
  }, [formData.market]);

  useEffect(() => {
    loadUser();
    loadMarkets();
  }, [loadUser, loadMarkets]);

  const validatePhoneNumber = (phone, countryCode) => {
    // Remove any non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (!digitsOnly) {
      return "Phone number is required";
    }

    if (countryCode === "+1") {
      // US: Exactly 10 digits
      if (digitsOnly.length !== 10) {
        return "US phone numbers must be exactly 10 digits";
      }
    } else if (countryCode === "+55") {
      // Brazil: 11 digits if third digit is 9, otherwise 10 digits
      if (digitsOnly.length >= 3 && digitsOnly[2] === '9') {
        if (digitsOnly.length !== 11) {
          return "Brazilian phone numbers with 9 as third digit must be 11 digits";
        }
      } else {
        if (digitsOnly.length !== 10) {
          return "Brazilian phone numbers must be 10 digits";
        }
      }
    }

    return "";
  };

  const handlePhoneChange = (value) => {
    // Allow only digits, spaces, dashes, and parentheses
    const cleaned = value.replace(/[^\d\s\-()]/g, '');
    setFormData(prev => ({ ...prev, phone: cleaned }));
    
    // Validate
    const error = validatePhoneNumber(cleaned, formData.country_code);
    setPhoneError(error);
  };

  const handleCountryCodeChange = (newCountryCode) => {
    setFormData(prev => ({ ...prev, country_code: newCountryCode }));
    
    // Re-validate with new country code
    if (formData.phone) {
      const error = validatePhoneNumber(formData.phone, newCountryCode);
      setPhoneError(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    const error = validatePhoneNumber(formData.phone, formData.country_code);
    if (error) {
      setPhoneError(error);
      return;
    }

    try {
    let finalLeadData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      preferred_name: formData.preferred_name || null,
      primary_email: formData.email,
      primary_phone: formData.country_code + formData.phone.replace(/\D/g, ''),
      origem: formData.source,
      market: formData.market,
      date_of_birth: formData.date_of_birth || null,
      gender: formData.gender || null,
      language: formData.language || null,
      country_of_birth: formData.country_of_birth || null,
      lifecycle_stage: 'new',
      address_line1: formData.address_line1 || null,
      address_line2: formData.address_line2 || null,
      city: formData.city || null,
      state: formData.state || null,
      postal_code: formData.postal_code || null,
      country: formData.country || null
    };
      
      // Generate custom ID if creating new lead
      if (!lead) {
        const { data } = await appClient.functions.invoke('autoGenerateId', {
          entity_type: 'lead'
        });
        
        if (data && data.custom_id) {
          finalLeadData.lead_id = data.custom_id;
          console.log("Generated lead_id:", data.custom_id);
        } else {
          throw new Error("Failed to generate custom ID for Lead");
        }
      } else {
        // If editing an existing lead, we assume the parent component's onSubmit or backend
        // will handle using the existing lead's ID for updates.
        // No modification to lead_id for existing leads here.
      }
      
      onSubmit(finalLeadData);
    } catch (error) {
      console.error("Error in lead form:", error);
      alert("Error creating lead: " + error.message);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Determinar se é criação (sem lead) ou edição (com lead)
  const isCreating = !lead;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Campos básicos - sempre mostrados */}
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name *</Label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            placeholder="John"
            required
            className="border-slate-300"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name *</Label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            placeholder="Doe"
            required
            className="border-slate-300"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="preferred_name">Preferred Name</Label>
          <Input
            id="preferred_name"
            value={formData.preferred_name}
            onChange={(e) => handleChange('preferred_name', e.target.value)}
            placeholder="Optional"
            className="border-slate-300"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="john@example.com"
            className="border-slate-300"
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <div className="flex gap-2">
            <Select
              value={formData.country_code}
              onValueChange={handleCountryCodeChange}
            >
              <SelectTrigger className="w-32 border-slate-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="+1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🇺🇸</span>
                    <span>+1</span>
                  </div>
                </SelectItem>
                <SelectItem value="+55">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🇧🇷</span>
                    <span>+55</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1">
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder={formData.country_code === "+1" ? "(555) 123-4567" : "11 98765-4321"}
                required
                className={`border-slate-300 ${phoneError ? 'border-rose-500' : ''}`}
              />
            </div>
          </div>
          {phoneError && (
            <Alert variant="destructive" className="mt-2 py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {phoneError}
              </AlertDescription>
            </Alert>
          )}
          <p className="text-xs text-slate-500 mt-1">
            {formData.country_code === "+1" 
              ? "Enter 10 digits (e.g., 5551234567)" 
              : "Brazilian numbers are 10-11 digits (e.g., 11 98765-4321 or 21 2345-6789)"}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="source">Source</Label>
          <Select
            value={formData.source}
            onValueChange={(value) => handleChange('source', value)}
          >
            <SelectTrigger className="border-slate-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableSources.map((source) => (
                <SelectItem key={source.id} value={source.id}>
                  {source.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="market">Market *</Label>
          <Select
            value={formData.market}
            onValueChange={(value) => handleChange('market', value)}
          >
            <SelectTrigger className="border-slate-300">
              <SelectValue placeholder="Select a market" />
            </SelectTrigger>
            <SelectContent>
              {(availableMarkets.length ? availableMarkets : ['US']).map((mkt) => (
                <SelectItem key={mkt} value={mkt}>
                  {mkt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Campos adicionais - apenas na edição */}
        {!isCreating && (
          <>
            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => handleChange('date_of_birth', e.target.value)}
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={formData.gender}
                onValueChange={(value) => handleChange('gender', value)}
              >
                <SelectTrigger className="border-slate-300">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <Select
                value={formData.language}
                onValueChange={(value) => handleChange('language', value)}
              >
                <SelectTrigger className="border-slate-300">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="english">English</SelectItem>
                  <SelectItem value="portuguese">Portuguese</SelectItem>
                  <SelectItem value="spanish">Spanish</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country_of_birth">Country of Birth</Label>
              <Select
                value={formData.country_of_birth}
                onValueChange={(value) => handleChange('country_of_birth', value)}
              >
                <SelectTrigger className="border-slate-300">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="United States">United States</SelectItem>
                  <SelectItem value="Brazil">Brazil</SelectItem>
                  <SelectItem value="Mexico">Mexico</SelectItem>
                  <SelectItem value="Canada">Canada</SelectItem>
                  <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                  <SelectItem value="Germany">Germany</SelectItem>
                  <SelectItem value="France">France</SelectItem>
                  <SelectItem value="Spain">Spain</SelectItem>
                  <SelectItem value="Italy">Italy</SelectItem>
                  <SelectItem value="Argentina">Argentina</SelectItem>
                  <SelectItem value="Colombia">Colombia</SelectItem>
                  <SelectItem value="Peru">Peru</SelectItem>
                  <SelectItem value="Chile">Chile</SelectItem>
                  <SelectItem value="Venezuela">Venezuela</SelectItem>
                  <SelectItem value="Ecuador">Ecuador</SelectItem>
                  <SelectItem value="Portugal">Portugal</SelectItem>
                  <SelectItem value="China">China</SelectItem>
                  <SelectItem value="India">India</SelectItem>
                  <SelectItem value="Japan">Japan</SelectItem>
                  <SelectItem value="South Korea">South Korea</SelectItem>
                  <SelectItem value="Australia">Australia</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>

      {/* Seção de endereço - apenas na edição */}
      {!isCreating && (
        <>
          <div className="col-span-2">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 mt-6">Address</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address_line1">Address Line 1</Label>
              <Input
                id="address_line1"
                value={formData.address_line1}
                onChange={(e) => handleChange('address_line1', e.target.value)}
                placeholder="Street address"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line2">Address Line 2</Label>
              <Input
                id="address_line2"
                value={formData.address_line2}
                onChange={(e) => handleChange('address_line2', e.target.value)}
                placeholder="Apartment, suite, etc. (optional)"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="City"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State/Province</Label>
              <Select
                value={formData.state}
                onValueChange={(value) => handleChange('state', value)}
              >
                <SelectTrigger className="border-slate-300">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {formData.country === 'United States' && (
                      <>
                        <SelectItem value="AL">Alabama</SelectItem>
                        <SelectItem value="AK">Alaska</SelectItem>
                        <SelectItem value="AZ">Arizona</SelectItem>
                        <SelectItem value="AR">Arkansas</SelectItem>
                        <SelectItem value="CA">California</SelectItem>
                        <SelectItem value="CO">Colorado</SelectItem>
                        <SelectItem value="CT">Connecticut</SelectItem>
                        <SelectItem value="DE">Delaware</SelectItem>
                        <SelectItem value="FL">Florida</SelectItem>
                        <SelectItem value="GA">Georgia</SelectItem>
                        <SelectItem value="HI">Hawaii</SelectItem>
                        <SelectItem value="ID">Idaho</SelectItem>
                        <SelectItem value="IL">Illinois</SelectItem>
                        <SelectItem value="IN">Indiana</SelectItem>
                        <SelectItem value="IA">Iowa</SelectItem>
                        <SelectItem value="KS">Kansas</SelectItem>
                        <SelectItem value="KY">Kentucky</SelectItem>
                        <SelectItem value="LA">Louisiana</SelectItem>
                        <SelectItem value="ME">Maine</SelectItem>
                        <SelectItem value="MD">Maryland</SelectItem>
                        <SelectItem value="MA">Massachusetts</SelectItem>
                        <SelectItem value="MI">Michigan</SelectItem>
                        <SelectItem value="MN">Minnesota</SelectItem>
                        <SelectItem value="MS">Mississippi</SelectItem>
                        <SelectItem value="MO">Missouri</SelectItem>
                        <SelectItem value="MT">Montana</SelectItem>
                        <SelectItem value="NE">Nebraska</SelectItem>
                        <SelectItem value="NV">Nevada</SelectItem>
                        <SelectItem value="NH">New Hampshire</SelectItem>
                        <SelectItem value="NJ">New Jersey</SelectItem>
                        <SelectItem value="NM">New Mexico</SelectItem>
                        <SelectItem value="NY">New York</SelectItem>
                        <SelectItem value="NC">North Carolina</SelectItem>
                        <SelectItem value="ND">North Dakota</SelectItem>
                        <SelectItem value="OH">Ohio</SelectItem>
                        <SelectItem value="OK">Oklahoma</SelectItem>
                        <SelectItem value="OR">Oregon</SelectItem>
                        <SelectItem value="PA">Pennsylvania</SelectItem>
                        <SelectItem value="RI">Rhode Island</SelectItem>
                        <SelectItem value="SC">South Carolina</SelectItem>
                        <SelectItem value="SD">South Dakota</SelectItem>
                        <SelectItem value="TN">Tennessee</SelectItem>
                        <SelectItem value="TX">Texas</SelectItem>
                        <SelectItem value="UT">Utah</SelectItem>
                        <SelectItem value="VT">Vermont</SelectItem>
                        <SelectItem value="VA">Virginia</SelectItem>
                        <SelectItem value="WA">Washington</SelectItem>
                        <SelectItem value="WV">West Virginia</SelectItem>
                        <SelectItem value="WI">Wisconsin</SelectItem>
                        <SelectItem value="WY">Wyoming</SelectItem>
                      </>
                    )}
                    {formData.country === 'Brazil' && (
                      <>
                        <SelectItem value="AC">Acre</SelectItem>
                        <SelectItem value="AL">Alagoas</SelectItem>
                        <SelectItem value="AP">Amapá</SelectItem>
                        <SelectItem value="AM">Amazonas</SelectItem>
                        <SelectItem value="BA">Bahia</SelectItem>
                        <SelectItem value="CE">Ceará</SelectItem>
                        <SelectItem value="DF">Distrito Federal</SelectItem>
                        <SelectItem value="ES">Espírito Santo</SelectItem>
                        <SelectItem value="GO">Goiás</SelectItem>
                        <SelectItem value="MA">Maranhão</SelectItem>
                        <SelectItem value="MT">Mato Grosso</SelectItem>
                        <SelectItem value="MS">Mato Grosso do Sul</SelectItem>
                        <SelectItem value="MG">Minas Gerais</SelectItem>
                        <SelectItem value="PA">Pará</SelectItem>
                        <SelectItem value="PB">Paraíba</SelectItem>
                        <SelectItem value="PR">Paraná</SelectItem>
                        <SelectItem value="PE">Pernambuco</SelectItem>
                        <SelectItem value="PI">Piauí</SelectItem>
                        <SelectItem value="RJ">Rio de Janeiro</SelectItem>
                        <SelectItem value="RN">Rio Grande do Norte</SelectItem>
                        <SelectItem value="RS">Rio Grande do Sul</SelectItem>
                        <SelectItem value="RO">Rondônia</SelectItem>
                        <SelectItem value="RR">Roraima</SelectItem>
                        <SelectItem value="SC">Santa Catarina</SelectItem>
                        <SelectItem value="SP">São Paulo</SelectItem>
                        <SelectItem value="SE">Sergipe</SelectItem>
                        <SelectItem value="TO">Tocantins</SelectItem>
                      </>
                    )}
                    {formData.country !== 'United States' && formData.country !== 'Brazil' && (
                      <SelectItem value="Other">Other</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  placeholder="ZIP or Postal Code"
                  className="border-slate-300"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => handleChange('country', value)}
                >
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Brazil">Brazil</SelectItem>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="Mexico">Mexico</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                    <SelectItem value="France">France</SelectItem>
                    <SelectItem value="Spain">Spain</SelectItem>
                    <SelectItem value="Italy">Italy</SelectItem>
                    <SelectItem value="Argentina">Argentina</SelectItem>
                    <SelectItem value="Colombia">Colombia</SelectItem>
                    <SelectItem value="Peru">Peru</SelectItem>
                    <SelectItem value="Chile">Chile</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}

      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
          className="border-slate-300"
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isProcessing || !!phoneError || !formData.market}
          className="bg-primary hover:bg-primary/90"
        >
          <Save className="w-4 h-4 mr-2" />
          {lead ? 'Update' : 'Create'} Lead
        </Button>
      </div>
    </form>
  );
}
